import apiClient from '../core';

export interface FinancialSummaryPayment {
  id: string;
  created: string;
  customer: string;
  description: string;
  amountCents: number;
}

export interface FinancialSummary {
  users: number | null;
  subscriptions: {
    active: number | null;
    trialing: number | null;
    pastDue: number | null;
  };
  collected: {
    thisMonthCents: number | null;
    allTimeCents: number | null;
  };
  balance: {
    availableCents: number | null;
    pendingCents: number | null;
  };
  recentPayments: FinancialSummaryPayment[];
  marshmallowsThisMonthCents: number | null;
  netThisMonthCents: number | null;
  payouts: {
    paidCents: number | null;
    next: { amountCents: number; arrivalDate: string } | null;
  };
  stripeError: string | null;
}

export const subscriptionsApi = {
  // Get pricing plans
  async getPlans(): Promise<any[]> {
    const response = await apiClient.get('/subscriptions/plans');
    return response.data;
  },

  // Get current subscription
  async getCurrentSubscription(): Promise<any> {
    const response = await apiClient.get('/subscriptions/current');
    return response.data;
  },

  // Create checkout session
  async createCheckoutSession(data: {
    planId: string;
    billingPeriod: 'monthly' | 'yearly';
    successUrl: string;
    cancelUrl: string;
  }): Promise<{ checkoutUrl: string }> {
    const response = await apiClient.post('/subscriptions/checkout', data);
    return response.data;
  },

  async createSetupCheckout(data: {
    planId: string;
    successUrl: string;
    cancelUrl: string;
  }): Promise<{ checkoutUrl: string }> {
    const response = await apiClient.post('/subscriptions/setup-checkout', data);
    return response.data;
  },

  async getOnboardingStatus(): Promise<{
    completed: boolean;
    hasPaymentMethod: boolean;
    requiresPaymentSetup: boolean;
  }> {
    const response = await apiClient.get('/subscriptions/onboarding');
    return response.data;
  },

  // Access customer portal
  async createPortalSession(data: { returnUrl: string }): Promise<{ portalUrl: string }> {
    const response = await apiClient.post('/subscriptions/portal', data);
    return response.data;
  },

  // Check feature access
  async checkFeature(feature: string): Promise<{ hasAccess: boolean }> {
    const response = await apiClient.get(`/subscriptions/check-feature/${feature}`);
    return response.data;
  },

  // Check if can create site
  async canCreateSite(): Promise<{ canCreate: boolean; reason?: string }> {
    const response = await apiClient.get('/subscriptions/can-create-site');
    return response.data;
  },

  // Check if can create page
  async canCreatePage(siteId: string): Promise<{ canCreate: boolean; reason?: string }> {
    const response = await apiClient.get(`/subscriptions/can-create-page/${siteId}`);
    return response.data;
  },

  async cancelSubscription(): Promise<{
    success: boolean;
    message: string;
    cancelAt?: string;
  }> {
    const response = await apiClient.post('/subscriptions/cancel');
    return response.data;
  },

  async updateSubscription(data: {
    planId: string;
    billingPeriod: 'monthly' | 'yearly';
  }): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/subscriptions/update', data);
    return response.data;
  },

  async getFinancialSummary(): Promise<FinancialSummary> {
    const response = await apiClient.get<FinancialSummary>('/subscriptions/financial-summary');
    return response.data;
  },
};
